import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { PresenterControls } from "@/components/PresenterControls";
import { PresenterSidebar } from "@/components/PresenterSidebar";
import { PresenterProgressBar } from "@/components/PresenterProgressBar";
import { PresenterWorkshopOverlay } from "@/workshop/PresenterWorkshopOverlay";
import { ArModerationsleiste } from "@/components/ArModerationsleiste";
import { StationsLeiste } from "@/components/StationsLeiste";
import { TimerPille } from "@/components/TimerPille";
import { usePhase, useWorkshop } from "@/state/store";
import { useSlideState } from "@/state/slide-context";
import { leinwandWechsel, maxFreieFolie } from "./presenter-phase";

const WORKSHOP_PHASES = new Set([
  "topics",
  "voting",
  "winner-reveal",
  "grouping",
  "working",
  "submitted",
  "evaluating",
  "evaluated",
  "reveal",
]);

export function PresenterPage() {
  const {
    deck: SLIDE_COMPONENTS,
    progressCuts,
    overnightAnnouncementSlide,
    waitingSlide,
    workshopOverlayPhases,
    soloTeams,
    stationsAblauf,
    themaSlide,
    workingSlide,
  } = useOffsiteConfig();
  const [params] = useSearchParams();
  const initialSlide = (() => {
    const n = Number(params.get("slide"));
    if (Number.isFinite(n) && n >= 1 && n <= SLIDE_COMPONENTS.length) {
      return n - 1;
    }
    return 0;
  })();
  const [index, setIndex] = useState(initialSlide);
  const [showControls, setShowControls] = useState(false);
  const [forceSlides, setForceSlides] = useState(false);
  const total = SLIDE_COMPONENTS.length;
  const slideCtx = useSlideState();

  const phase = usePhase();
  const anzeigePhase = useWorkshop().phase.anzeigePhase;
  /**
   * Freies Blättern (Tasten, Klicker, Pfeile) endet am Horizont des
   * erreichten Schritts (maxFreieFolie, Fund 17.09.) — sonst blättert man
   * aus dem Thema in Arbeitsphasen-/Bau-Folien mit leerer Uhr oder aus dem
   * Vortrag in den Abschluss. Die Leiste selbst springt ungeclampt
   * (leinwandAuf).
   */
  const maxFrei = stationsAblauf
    ? maxFreieFolie(phase, anzeigePhase, {
        themaSlide: themaSlide ?? 0,
        workingSlide: workingSlide ?? 0,
        vortragSlides: stationsAblauf.vortragSlides,
        abschlussSlide: stationsAblauf.abschlussSlide,
        deckLaenge: SLIDE_COMPONENTS.length,
      })
    : SLIDE_COMPONENTS.length - 1;
  // Offsites dürfen einschränken, wann die Leinwand die Workshop-Oberfläche
  // zeigt. Ohne Angabe bleibt es beim bisherigen Verhalten.
  const overlayPhases = useMemo(
    () => (workshopOverlayPhases ? new Set(workshopOverlayPhases) : WORKSHOP_PHASES),
    [workshopOverlayPhases],
  );
  const workshopActive = overlayPhases.has(phase) && !forceSlides;

  // Spiegel den lokalen Index in den globalen SlideContext, damit die
  // Sidebar (auch von /workshop/* aus) wissen kann wo wir sind.
  useEffect(() => {
    slideCtx.reportIndex(index);
    slideCtx.reportTotal(total);
  }, [index, total, slideCtx]);

  // Was die Leinwand beim Phasenwechsel tut, entscheidet leinwandWechsel()
  // — als reine Funktion prüfbar, siehe presenter-phase.test.ts.
  const prevPhaseRef = useRef<string>(phase);
  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;

    const wechsel = leinwandWechsel(prev, phase, {
      overlayPhases,
      waitingSlide,
      overnightSlide: overnightAnnouncementSlide,
      // Stations-Ablauf: der automatische Sprung Generierung → Ergebnisse
      // lässt die V-Folie stehen, bis die Moderation „Ergebnisse" klickt.
      revealManuell: stationsAblauf !== undefined,
    });
    if (wechsel.forceSlides !== undefined) setForceSlides(wechsel.forceSlides);
    if (wechsel.index !== undefined) setIndex(wechsel.index);
  }, [phase, overnightAnnouncementSlide, waitingSlide, overlayPhases, stationsAblauf]);

  const Slide = useMemo(
    () => SLIDE_COMPONENTS[index] ?? SLIDE_COMPONENTS[0],
    [SLIDE_COMPONENTS, index],
  );

  // Toggle <html class="presenter-mode"> so the design's overflow:hidden
  // returns for the fullscreen .shell flipper without breaking other pages.
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add("presenter-mode");
    return () => {
      html.classList.remove("presenter-mode");
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't intercept arrows when typing in form fields.
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;

      // Presenter-Klicker senden PageDown/PageUp — gleichwertig zu ←/→.
      if (e.key === "ArrowRight" || e.code === "Space" || e.key === "PageDown") {
        if (!workshopActive) setIndex((i) => Math.min(maxFrei, i + 1));
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        if (!workshopActive) setIndex((i) => Math.max(0, i - 1));
      } else if (e.key.toLowerCase() === "f") {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen().catch(() => undefined);
      } else if (e.key.toLowerCase() === "c") {
        setShowControls((s) => !s);
      } else if (e.key.toLowerCase() === "w") {
        setForceSlides((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total, workshopActive, maxFrei]);

  // Two stable section "phases" — slides vs. workshop. Toggling .active
  // gives the section-switch animation defined in design/app.css.
  return (
    <div className="shell">
      <PresenterSidebar
        slideIndex={index}
        slideTotal={total}
        onJumpSlide={(i) => setIndex(i)}
        workshopActive={workshopActive}
        forceSlides={forceSlides}
        onToggleForceSlides={(next) => setForceSlides(next)}
      />

      {/* Mit Moderationsleiste wird die Folienfläche kürzer, statt von der
       *  Leiste überdeckt zu werden — sonst liegt sie über dem Fuß der Folie,
       *  und „Thema nutzen" verschwindet darunter. */}
      <main className={`canvas${soloTeams ? " hat-leiste" : ""}`}>
        <section
          className={`section${!workshopActive ? " active" : ""}`}
          aria-hidden={workshopActive}
        >
          <Slide
            index={index}
            total={total}
            onJump={(n) => setIndex(Math.max(0, Math.min(total - 1, n - 1)))}
          />
        </section>
        <section
          className={`section${workshopActive ? " active" : ""}`}
          aria-hidden={!workshopActive}
        >
          {workshopActive && <PresenterWorkshopOverlay />}
        </section>

        {/* Zarte Blätter-Pfeile am Rand — für Maus/Touch am Pult; der
         *  Presenter-Klicker läuft über PageUp/PageDown. */}
        {!workshopActive && (
          <>
            <button
              type="button"
              className="folien-pfeil folien-pfeil--zurueck"
              aria-label="Vorherige Folie"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index === 0}
            >
              ‹
            </button>
            <button
              type="button"
              className="folien-pfeil folien-pfeil--weiter"
              aria-label="Nächste Folie"
              onClick={() => setIndex((i) => Math.min(maxFrei, i + 1))}
              disabled={index >= maxFrei}
            >
              ›
            </button>
          </>
        )}

        {showControls && (
          <PresenterControls
            index={index}
            total={total}
            onPrev={() => setIndex((i) => Math.max(0, i - 1))}
            onNext={() => setIndex((i) => Math.min(total - 1, i + 1))}
            onJump={(i) => setIndex(i)}
          />
        )}

        {/* Moderationsleiste über der Fortschrittsleiste: Der Moderator fährt
         *  einen Bildschirm und klappt die Navigation zum Vortrag ein — der
         *  Ablauf muss deshalb neben der Navigation liegen, nicht in ihr. */}
        {soloTeams &&
          (stationsAblauf ? (
            <StationsLeiste
              slideIndex={index}
              onJumpSlide={(i) => setIndex(i)}
              onToggleForceSlides={(next) => setForceSlides(next)}
              forceSlides={forceSlides}
            />
          ) : (
            <ArModerationsleiste
              slideIndex={index}
              onJumpSlide={(i) => setIndex(i)}
              onToggleForceSlides={(next) => setForceSlides(next)}
            />
          ))}

        {/* Timer-Overlay unten rechts — nur der Stations-Ablauf führt die
         *  servergeführten Uhren (Briefing Prompt-Teil 4.1). */}
        {stationsAblauf && <TimerPille />}

        {/* Zwei Fortschrittsanzeigen zählen zwei verschiedene Dinge: die Leiste
         *  Schritte, diese Leiste Folien. Bei 30 Vertriebs-Folien ist die
         *  Folien-Zählung hilfreich, bei sieben und einem Fünf-Schritt-Ablauf
         *  ist sie Rauschen — und stand als „Folie 3 / 7" direkt neben
         *  „Schritt 2 von 5". */}
        {!soloTeams && (
          <PresenterProgressBar
            slideIndex={index}
            slideTotal={total}
            workshopActive={workshopActive}
            cuts={progressCuts}
            onJump={(i) => setIndex(i)}
          />
        )}
      </main>
    </div>
  );
}
